import dayjs from 'dayjs';

import { ROOM_OPENING_DISCOUNT_MODE } from 'app/typings/core/extranet/inventory/enums/RoomOpeningDiscountMode';

export type AutoInventoryBadgeVariant = 'active' | 'deactivated';

export type PricingRuleBadgeVariant = 'lastMinute' | 'default' | 'deactivated';

export type AutoInventoryBadge = {
  type: 'autoInventory';
  variant: AutoInventoryBadgeVariant;
  color: 'blue';
  tooltip: string;
};

export type PricingRuleBadge = {
  type: 'pricingRule';
  variant: PricingRuleBadgeVariant;
  color: 'blue' | 'gray';
  tooltip: string;
};

export type InventoryBadge = AutoInventoryBadge | PricingRuleBadge;

export type AutoInventoryBadgeParams = {
  stockScrapperEnabled: boolean;
  bookingcomId?: number;
  isAutomated?: boolean;
  value?: number;
};

export const getAutoInventoryBadge = (
  params: AutoInventoryBadgeParams
): AutoInventoryBadge | null => {
  const { stockScrapperEnabled, bookingcomId, isAutomated, value } = params;

  if (!stockScrapperEnabled || !bookingcomId) {
    return null;
  }

  const isAutoControlled = isAutomated || value === undefined;

  return isAutoControlled
    ? {
        type: 'autoInventory',
        variant: 'active',
        color: 'blue',
        tooltip: 'Auto inventory activated',
      }
    : {
        type: 'autoInventory',
        variant: 'deactivated',
        color: 'blue',
        tooltip: 'Auto inventory deactivated by manual edition',
      };
};

export type PricingRuleBadgeParams = {
  stockScrapperEnabled: boolean;
  bookingcomId?: number;
  discountMode?: ROOM_OPENING_DISCOUNT_MODE;
  discountPrice?: number;
  lastMinuteDiscount?: number;
  lastMinuteDays?: number;
  date: string;
};

export const getPricingRuleBadge = (
  params: PricingRuleBadgeParams
): PricingRuleBadge | null => {
  const {
    stockScrapperEnabled,
    bookingcomId,
    discountMode,
    lastMinuteDiscount,
    lastMinuteDays,
    discountPrice,
    date,
  } = params;

  if (discountMode === undefined) {
    return null;
  }

  const iconColor = stockScrapperEnabled && !!bookingcomId ? 'blue' : 'gray';

  const dateObj = dayjs(date);
  const hasLastMinute =
    lastMinuteDiscount !== undefined && lastMinuteDays !== undefined;
  const isInLastMinuteDiscount =
    hasLastMinute &&
    dateObj.isBefore(dayjs().add(lastMinuteDays + 1, 'days'), 'days') &&
    !dateObj.isBefore(dayjs(), 'days');

  if (
    discountMode === ROOM_OPENING_DISCOUNT_MODE.MANUAL &&
    discountPrice !== undefined
  ) {
    return {
      type: 'pricingRule',
      variant: 'deactivated',
      color: iconColor,
      tooltip: 'Default discount deactivated by manual edition',
    };
  }

  if (
    discountMode === ROOM_OPENING_DISCOUNT_MODE.LAST_MINUTE_AUTO ||
    isInLastMinuteDiscount
  ) {
    return {
      type: 'pricingRule',
      variant: 'lastMinute',
      color: iconColor,
      tooltip: 'Last minute discount activated',
    };
  }

  return {
    type: 'pricingRule',
    variant: 'default',
    color: iconColor,
    tooltip: 'Default discount activated',
  };
};
